import React from 'react';
import { Button, ButtonProps, OverlayTrigger, Tooltip } from 'react-bootstrap';

import { translateComponent } from '@/utils/tolgee/translateComponent';

/** Where an account goes to get the plan that includes the feature. */
const UPGRADE_PATH = '/subscription-management/select/';

export interface LomavisPlanLockedButton1Props
  extends Omit<ButtonProps, 'children'> {
  /** Whether the account's plan includes what this button does. */
  isAvailable: boolean;
  /** Why it is locked, in one line. Falls back to a generic explanation. */
  lockedReason?: string;
  children?: React.ReactNode;
}

/**
 * A button that stays visible on plans that cannot use it.
 *
 * Hiding an action leaves no trace of the feature: someone who would pay for
 * it never learns it exists, and someone who had it on another plan thinks it
 * broke. So the button keeps its place and wears a lock, says why when hovered,
 * and leads to the upgrade rather than doing nothing.
 *
 * Deliberately not `disabled`: a disabled button takes no pointer events, so
 * it can carry neither the tooltip that explains the lock nor the click that
 * resolves it.
 */
export const LomavisPlanLockedButton1: React.FC<
  LomavisPlanLockedButton1Props
> = ({
  isAvailable,
  lockedReason,
  children,
  variant = 'secondary',
  size = 'sm',
  className = '',
  onClick,
  disabled,
  ...rest
}) => {
  const t = translateComponent('LomavisPlanLockedButton1');

  if (isAvailable) {
    return (
      <Button
        variant={variant}
        size={size}
        className={className}
        onClick={onClick}
        disabled={disabled}
        {...rest}
      >
        {children}
      </Button>
    );
  }

  return (
    <OverlayTrigger
      placement="top"
      overlay={
        <Tooltip>{lockedReason ?? t('notInYourPlan')}</Tooltip>
      }
    >
      <Button
        variant={variant}
        size={size}
        className={`d-inline-flex align-items-center gap-2 ${className}`}
        href={UPGRADE_PATH}
        {...rest}
      >
        <i className="fa-duotone fa-lock" aria-hidden="true" />
        {children}
      </Button>
    </OverlayTrigger>
  );
};
